import {
  array,
  number,
  object,
  ObjectSchema,
  string,
  ValidationError,
} from 'yup';
import { setIn } from 'final-form';
import { LinkDeliveryMethod } from './services/sendLinkManager';
import { NAME_REGEX, EMAIL_REGEX } from '@chargeafter/validators';
import { orderFormDetails } from './constants';

export const mobileValidationRegexByCurrency = (currency: string) => {
  switch (currency) {
    case 'AUD':
      return /^04.*/;
    default:
      return /^[0-9]{10}$/;
  }
};

export const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!""#$%&'()*+,-./:;<=>?@[\]^_`{|}~]).{8,}$/;

export const validateData = async (
  data: unknown,
  schema: ObjectSchema<any>,
) => {
  try {
    await schema.validate(data, { abortEarly: false });
    return;
  } catch (e) {
    return e;
  }
};

export const applyResultSchema = object()
  .required()
  .shape({
    consumerId: string().required(),
    consumerDetails: object().required().shape({
      firstName: string().required(),
      lastName: string().required(),
      email: string().required(),
      mobilePhoneNumber: string(),
    }),
    availableCredit: array()
      .required()
      .of(
        object()
          .required()
          .shape({
            lender: object().required().shape({
              id: string().required(),
              name: string().required(),
            }),
            creditAmount: number(),
            minimumAmount: number().nullable(),
            financialProductType: string().nullable(),
          }),
      ),
    applicationId: string().required(),
    lendersStatus: array().of(
      object().shape({
        name: string(),
        id: string().required(),
        status: string().required(),
      }),
    ),
  });

export const sendLinkFormSchema = (
  mobileRegex: RegExp,
  deliveryMethod: LinkDeliveryMethod,
) =>
  object().shape({
    firstName: string()
      .required('Please enter first name')
      .matches(NAME_REGEX, 'Please enter valid name'),
    lastName: string()
      .required('Please enter last name')
      .matches(NAME_REGEX, 'Please enter valid name'),
    ...(deliveryMethod !== LinkDeliveryMethod.Sms && {
      email: string()
        .email('Please enter valid email')
        .required('Please enter email')
        .matches(EMAIL_REGEX, 'Please enter valid email'),
    }),
    ...(deliveryMethod === LinkDeliveryMethod.Sms && {
      mobilePhoneNumber: string()
        .required('Please enter phone number')
        .matches(mobileRegex, { message: 'Please enter valid phone number' }),
    }),
    autoSettle: string().when(['isCheckout', 'settleSelection'], {
      is: (isCheckout: boolean, settleSelection: boolean) =>
        isCheckout && settleSelection,
      then: () =>
        string()
          .oneOf(['Automatic', 'Manual'])
          .required('Please select preference'),
    }),
  });

export const validateCartItem = (
  values: Record<string, any>,
  showWarranty: boolean,
) => {
  let errors = {};

  Object.keys(values)
    .filter((x) => x !== 'leasable')
    .forEach((key) => {
      if (!values[key] || +values[key] === 0) {
        const displayName = key.charAt(0).toUpperCase() + key.slice(1);

        errors = setIn(errors, key, `Item ${displayName} is required`);
      }
    });
  if (showWarranty) {
    Object.keys(values.warranty).forEach((key) => {
      if (!values.warranty[key]) {
        const displayName = key.charAt(0).toUpperCase() + key.slice(1);
        errors = setIn(
          errors,
          `warranty.${key}`,
          `Warranty ${displayName} is required`,
        );
      }
    });
  }
  return errors;
};

const singleCartItemValidationSchema = object().shape({
  merchantOrderId: string()
    .when('merchantOrderIdRequired', {
      is: true,
      then: string().required('Merchant Order Id is required'),
      otherwise: string().optional(),
    })
    .max(24, ({ max }) => `More than ${max} characters are not allowed.`)
    .matches(
      orderFormDetails.orderId.validations.invalidCharacters?.regex || /./,
      orderFormDetails.orderId.validations.invalidCharacters?.errorMessage,
    ),
  singleItemName: string()
    .optional()
    .max(100, ({ max }) => `More than ${max} characters are not allowed`),
});

export const isCartValid = (
  values: Record<string, unknown>,
  fieldName?: string,
) => {
  try {
    fieldName
      ? singleCartItemValidationSchema.validateSyncAt(fieldName, values)
      : singleCartItemValidationSchema.validateSync(values);
    return true;
  } catch (error) {
    if (error instanceof ValidationError) {
      if (fieldName) {
        return error;
      } else {
        return false;
      }
    }
    throw error;
  }
};
